/** Sync reader chat with Nevisign studio contract chat APIs. */

import { nevisignFetch } from "@nevisign/reader-core";
import type { ReaderChatMessage } from "./chatStore";

const USER_KEY_STORAGE = "clm_chat_user_key";

export function getReaderChatUserKey(): string {
  try {
    const existing = localStorage.getItem(USER_KEY_STORAGE);
    if (existing && existing.length >= 8) return existing;
    const next = `u_${crypto.randomUUID().replace(/-/g, "").slice(0, 16)}`;
    localStorage.setItem(USER_KEY_STORAGE, next);
    return next;
  } catch {
    return `u_anon_${Date.now().toString(36)}`;
  }
}

export type CloudChatMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  at?: string;
};

export type CloudChatSession = {
  contract_id: string;
  user_key: string;
  messages: CloudChatMessage[];
  updated_at?: string;
};

function toReaderMessages(msgs: CloudChatMessage[]): ReaderChatMessage[] {
  return msgs
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => ({
      role: m.role as "user" | "assistant",
      content: m.content || "",
    }))
    .filter((m) => m.content.trim().length > 0);
}

/** GET /api/contracts/:id/chat — load studio history for this document. */
export async function fetchCloudChat(
  apiBase: string,
  documentId: string
): Promise<ReaderChatMessage[] | null> {
  const id = documentId.trim();
  if (!apiBase.trim() || !id) return null;
  const userKey = getReaderChatUserKey();
  try {
    const res = await nevisignFetch(
      apiBase,
      `/api/contracts/${encodeURIComponent(id)}/chat?user_key=${encodeURIComponent(userKey)}`,
      {
        method: "GET",
        headers: { "X-Chat-User": userKey },
        cache: "no-store",
      },
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      session?: CloudChatSession;
      error?: string;
    };
    const msgs = json.session?.messages;
    if (!Array.isArray(msgs)) return null;
    return toReaderMessages(msgs);
  } catch {
    return null;
  }
}

/** PUT /api/contracts/:id/chat — persist reader messages into studio session. */
export async function pushCloudChat(
  apiBase: string,
  documentId: string,
  messages: ReaderChatMessage[]
): Promise<boolean> {
  const id = documentId.trim();
  if (!apiBase.trim() || !id) return false;
  const userKey = getReaderChatUserKey();
  const payload = messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m, i) => ({
      id: `reader-${i}-${Date.now().toString(36)}`,
      role: m.role,
      content: m.content,
      at: new Date().toISOString(),
    }));
  try {
    const res = await nevisignFetch(
      apiBase,
      `/api/contracts/${encodeURIComponent(id)}/chat`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Chat-User": userKey,
        },
        body: JSON.stringify({
          user_key: userKey,
          messages: payload,
          source: "nevisign-reader",
        }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}
